import { type JSX, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { VMQuotes } from "@4pace-cpq/core/api";
import { TextField, debounce } from "@mui/material";
import Box from "@mui/material/Box";
import { useQueryClient } from "@tanstack/react-query";

import { LabeledText } from "@/cpq/overrides/LabeledText/LabeledText";
import { useConfigurationServiceGetCustomApiConfigurationPublicShoppingCartByQuoteIdExtendedDataKey } from "@/openapi/api-cpq/queries/common";
import {
  useConfigurationServiceGetCustomApiConfigurationPublicShoppingCartByQuoteIdExtendedData,
  useConfigurationServicePutCustomApiConfigurationPublicShoppingCartByQuoteIdExtendedData,
} from "@/openapi/api-cpq/queries/queries";

import { AdditionalQuoteTextBlock } from "./AdditionalQuoteTextBlock";

export default function AdditionalQuoteFields({
  item,
}: {
  item: VMQuotes;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const userSessionId = window.sessionStorage.getItem("userSessionId");
  const { data: extendedData } =
    useConfigurationServiceGetCustomApiConfigurationPublicShoppingCartByQuoteIdExtendedData(
      { quoteId: item.id },
    );

  const { mutate: setExtendedData } =
    useConfigurationServicePutCustomApiConfigurationPublicShoppingCartByQuoteIdExtendedData(
      {},
    );

  const [quoteFieldsDraft, setQuoteFieldsDraft] = useState<{
    quoteId: VMQuotes["id"];
    projectName: string;
    projectLocation: string;
  } | null>(null);

  const projectName =
    quoteFieldsDraft?.quoteId === item.id
      ? quoteFieldsDraft.projectName
      : (extendedData?.projectName ?? "");

  const projectLocation =
    quoteFieldsDraft?.quoteId === item.id
      ? quoteFieldsDraft.projectLocation
      : (extendedData?.projectLocation ?? "");

  const sendExtendedDataChange = useCallback(
    (nextProjectName: string, nextProjectLocation: string) => {
      if (userSessionId) {
        setExtendedData(
          {
            quoteId: item.id,
            requestBody: {
              projectName: nextProjectName,
              projectLocation: nextProjectLocation,
              userSessionId: userSessionId,
            },
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: [
                  useConfigurationServiceGetCustomApiConfigurationPublicShoppingCartByQuoteIdExtendedDataKey,
                ],
              });
            },
          },
        );
      }
    },
    [item.id, userSessionId, setExtendedData, queryClient],
  );

  const debouncedExtendedDataChange = useMemo(() => {
    return debounce(sendExtendedDataChange, 500);
  }, [sendExtendedDataChange]);

  function handleProjectNameChange(value: string): void {
    setQuoteFieldsDraft({
      quoteId: item.id,
      projectName: value,
      projectLocation,
    });
    debouncedExtendedDataChange(value, projectLocation);
  }

  function handleProjectLocationChange(value: string): void {
    setQuoteFieldsDraft({
      quoteId: item.id,
      projectName,
      projectLocation: value,
    });
    debouncedExtendedDataChange(projectName, value);
  }

  return (
    <Box>
      <AdditionalQuoteTextBlock item={item} />
      <Box sx={{ display: "flex", gap: "1rem" }}>
        <LabeledText
          label={
            t("regiolux:label.additionalQuoteFields.projectDescription") + "*"
          }
          text={
            <TextField
              value={projectName}
              onChange={(e) => {
                handleProjectNameChange(e.target.value);
              }}
              fullWidth
            />
          }
        />
        <LabeledText
          label={
            t("regiolux:label.additionalQuoteFields.projectLocation") + "*"
          }
          text={
            <TextField
              value={projectLocation}
              onChange={(e) => {
                handleProjectLocationChange(e.target.value);
              }}
              fullWidth
            />
          }
        />
      </Box>
    </Box>
  );
}
